import { Stack, Typography } from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import { useMemo } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';

interface FeatureInfo {
  featureId: string;
  content: string;
  sortOrder: number;
  tooltip?: string;
}

interface ProductMembership {
  displayName: string;
  monthlyPriceCents: number | null;
  annualPriceCents: number | null;
  callForPricing?: boolean;
  features: FeatureInfo[];
}

interface FeaturesProps {
  featureMemberships: ProductMembership[];
  showMonthlyPrices: boolean;
  showAnnualPrices: boolean;
}

const centsToDisplay = (cents: number) => (cents / 100).toFixed(2);

function formatPriceLabel({
  monthlyPriceCents,
  annualPriceCents,
  showMonthlyPrices,
  showAnnualPrices,
  callForPricing,
}: {
  monthlyPriceCents: number | null;
  annualPriceCents: number | null;
  showMonthlyPrices: boolean;
  showAnnualPrices: boolean;
  callForPricing?: boolean;
}): string {
  if (callForPricing) {
    return 'Call for pricing';
  }
  const parts: string[] = [];
  if (showMonthlyPrices && monthlyPriceCents) {
    parts.push(`Monthly: $${centsToDisplay(monthlyPriceCents)}`);
  }
  if (showAnnualPrices && annualPriceCents) {
    parts.push(`Annual: $${centsToDisplay(annualPriceCents)}`);
  }
  return parts.length ? parts.join(' / ') : '';
}

interface ProductMembershipCardProps {
  membership: ProductMembership;
  showMonthlyPrices: boolean;
  showAnnualPrices: boolean;
}

const ProductMembershipCard = ({
  membership,
  showMonthlyPrices,
  showAnnualPrices,
}: ProductMembershipCardProps) => {
  const priceLabel = useMemo(
    () =>
      formatPriceLabel({
        monthlyPriceCents: membership.monthlyPriceCents ?? null,
        annualPriceCents: membership.annualPriceCents ?? null,
        showMonthlyPrices,
        showAnnualPrices,
        callForPricing: membership.callForPricing,
      }),
    [
      membership.monthlyPriceCents,
      membership.annualPriceCents,
      membership.callForPricing,
      showMonthlyPrices,
      showAnnualPrices,
    ],
  );

  return (
    <Stack>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
        }}
      >
        {formatMembershipName(membership.displayName)}
      </Typography>
      <ul
        style={{
          marginTop: 0,
        }}
      >
        {priceLabel ? (
          <li>
            <Typography variant="subtitle2">{priceLabel}</Typography>
          </li>
        ) : null}
        {membership.features.map((feature) => (
          <li key={`${feature.featureId}:${feature.sortOrder}`}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  textWrap: 'pretty',
                }}
                dangerouslySetInnerHTML={{
                  __html: feature.content,
                }}
              />
              {!!feature.tooltip && (
                <Tooltip
                  nonInteractable={false}
                  title={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: feature.tooltip,
                      }}
                    />
                  }
                >
                  <DynamicIcon
                    fontSize="1rem"
                    name="info"
                    sx={{
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </li>
        ))}
      </ul>
    </Stack>
  );
};

export const MembershipFeatures = ({
  featureMemberships,
  showMonthlyPrices,
  showAnnualPrices,
}: FeaturesProps) => {
  return (
    <>
      {featureMemberships.map((membership) => (
        <ProductMembershipCard
          key={membership.displayName}
          membership={membership}
          showMonthlyPrices={showMonthlyPrices}
          showAnnualPrices={showAnnualPrices}
        />
      ))}
    </>
  );
};
